import { graphemeLength, sliceGraphemes } from '@up2stay/up2promo-commons';
import { Label } from '@up2stay/up2promo-ui';
import { useState } from 'react';

import type { ModuleEntryGovernanceProps } from '@/core/components/profile/module-entry-governance';
import {
  useEntryGovernance,
  WholeModuleGovernanceHeader,
} from '@/core/components/profile/module-entry-governance-ui';
import { TextareaWithCharCount } from '@/core/components/ui/field-with-char-count';
import { MODULE_ENTRY_WHOLE_MODULE_KEY } from '@/core/lib/profile-module-entry-policies';
import { useDictionary } from '@/core/providers/dictionary-provider';
import type { BiographyModuleConfig } from '@/core/types/profile-modules';

const BIOGRAPHY_MAX_LENGTH = 1500;

interface BiographyModuleConfigProps extends ModuleEntryGovernanceProps {
  config: BiographyModuleConfig;
  onChange: (config: BiographyModuleConfig) => void;
  fieldLockedHintMessage?: string;
}

export function BiographyModuleConfigComponent({
  config,
  onChange,
  contentReadOnly = false,
  templateModule,
  showEntryPolicies = false,
  onEntryPolicyChange,
  policyLabels,
  fieldLockedHintMessage,
  workspaceId,
  templateId,
  publishStatus,

  memberRoster,
  memberRosterLoading,
  hasAssignedTeams,
  memberValuesDict,
}: BiographyModuleConfigProps) {
  const bio = useDictionary().profiles.edit.modules.biography;
  const [localConfig, setLocalConfig] = useState<BiographyModuleConfig>(config);
  const [prevConfig, setPrevConfig] = useState(config);
  if (config !== prevConfig) {
    setPrevConfig(config);
    setLocalConfig(config);
  }
  const { entryLocked } = useEntryGovernance({
    templateModule,
    entryId: MODULE_ENTRY_WHOLE_MODULE_KEY,
  });

  const handleChange = (updates: Partial<BiographyModuleConfig>) => {
    if (contentReadOnly) return;
    const newConfig = { ...localConfig, ...updates };
    setLocalConfig(newConfig);
    onChange(newConfig);
  };

  const content = localConfig.content || '';

  const setContent = (value: string) => {
    handleChange({
      content:
        graphemeLength(value) <= BIOGRAPHY_MAX_LENGTH
          ? value
          : sliceGraphemes(value, BIOGRAPHY_MAX_LENGTH),
    });
  };

  return (
    <div className="space-y-2">
      <WholeModuleGovernanceHeader
        templateModule={templateModule}
        showEntryPolicy={showEntryPolicies}
        onEntryPolicyChange={onEntryPolicyChange}
        policyLabels={policyLabels}
        fieldLockedHintMessage={fieldLockedHintMessage}
        entryLabel="Biografie"
        memberDialogTitle="Biografie"
        memberValueColumn={{
          kind: 'moduleEntry',
          entryId: '__module__',
          moduleId: templateModule?.id,
          label: 'Biografie',
        }}
        memberDefaultValue={content}
        memberInputMaxLength={BIOGRAPHY_MAX_LENGTH}
        workspaceId={workspaceId}
        templateId={templateId}
        publishStatus={publishStatus}
        memberRoster={memberRoster}
        memberRosterLoading={memberRosterLoading}
        hasAssignedTeams={hasAssignedTeams}
        memberValuesDict={memberValuesDict}
        canManage={!contentReadOnly}
      />
      <Label htmlFor="biography-content">{bio.contentLabel}</Label>
      <TextareaWithCharCount
        id="biography-content"
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder={templateId ? bio.placeholderTemplate : bio.placeholder}
        rows={6}
        maxLength={BIOGRAPHY_MAX_LENGTH}
        valueLength={graphemeLength(content)}
        className="w-full min-h-0"
        disabled={contentReadOnly || (entryLocked && !showEntryPolicies)}
      />
    </div>
  );
}
